/**
 * Product Card Carousel — snippet-author runtime.
 *
 * Responsibilities:
 *   1. Scroll position → progress thumb transform, tracking the first card in
 *      view.
 *   2. Prev / next arrow handlers (one card per click, clamped at the ends —
 *      no wrap; arrows disable when every card already fits).
 *   3. Quick-add progressive enhancement: intercept the native cart-add form
 *      and add via the Spectrum cart SDK in-place when it is present; without
 *      it the native form POST proceeds (works with JS disabled).
 *   4. Card impression + click + quick-add analytics.
 *
 * Container-scoped: every DOM read/write goes through `node` so multiple
 * carousels on the same page never collide.
 */
;(() => {
  const SNIPPET_ID = 'jaqo0kgp'
  const FEATURE_SLUG = 'product_card_carousel'
  const ROW_SELECTOR = `.sai-${SNIPPET_ID}__row`
  const CARD_SELECTOR = `.sai-${SNIPPET_ID}__card`
  const CARD_LINK_SELECTOR = `.sai-${SNIPPET_ID}__card-link`
  const TITLE_SELECTOR = `.sai-${SNIPPET_ID}__title`
  const PROGRESS_SELECTOR = `.sai-${SNIPPET_ID}__progress`
  const THUMB_SELECTOR = '[data-spectrum-progress-thumb]'
  const ARROW_PREV_SELECTOR = '[data-spectrum-arrow="prev"]'
  const ARROW_NEXT_SELECTOR = '[data-spectrum-arrow="next"]'
  const QUICK_ADD_SELECTOR = '[data-spectrum-quick-add]'

  function noopTrack() {}

  // Wrap track so a malformed payload or downstream throw can never break the
  // carousel. Telemetry is observational — surface the failure in DevTools but
  // keep scroll / nav / add-to-cart working.
  function safeTrack(track) {
    return (name, payload) => {
      try {
        track(name, payload)
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn(`[${FEATURE_SLUG}] analytics dispatch failed`, name, err)
      }
    }
  }

  // Find the card whose left edge is closest to the row's left content edge —
  // i.e. the first card "in view". Row-relative (not viewport-relative) so it
  // reads 0 on load even when a non-overflowing group is centered, and still
  // tracks correctly once the row actually scrolls. Returns 0 if no cards.
  function currentIndex(row, cards) {
    if (!cards || cards.length === 0) return 0
    const rowLeft = row.getBoundingClientRect().left
    let bestIndex = 0
    let bestDistance = Number.POSITIVE_INFINITY
    for (let i = 0; i < cards.length; i++) {
      const d = Math.abs(cards[i].getBoundingClientRect().left - rowLeft)
      if (d < bestDistance) {
        bestDistance = d
        bestIndex = i
      }
    }
    return bestIndex
  }

  function cardTitle(card) {
    return card?.querySelector(TITLE_SELECTOR)?.textContent?.trim() || ''
  }

  function cardUrl(card) {
    return card?.querySelector(CARD_LINK_SELECTOR)?.getAttribute('href') || ''
  }

  function initCarousel(node, track) {
    const row = node.querySelector(ROW_SELECTOR)
    if (!row) return
    const cards = Array.from(row.querySelectorAll(`:scope > li > ${CARD_SELECTOR}`))
    if (cards.length === 0) return

    const total = cards.length
    const thumb = node.querySelector(THUMB_SELECTOR)
    const progress = node.querySelector(PROGRESS_SELECTOR)
    const prevBtn = node.querySelector(ARROW_PREV_SELECTOR)
    const nextBtn = node.querySelector(ARROW_NEXT_SELECTOR)

    // Force the row's overflow + display via inline `!important`. Themes that
    // override `ul { overflow: visible }` or `ul { display: block }` can win
    // against author CSS in some cascade orderings — inline `!important` is the
    // strongest layer the runtime can install.
    row.style.setProperty('display', 'flex', 'important')
    row.style.setProperty('overflow-x', 'auto', 'important')
    row.style.setProperty('overflow-y', 'hidden', 'important')

    // True only when the row has horizontal overflow — i.e. there are more cards
    // than fit. When every card fits (the common 4-up desktop case) there is
    // nothing to navigate, so the arrows are disabled.
    const isScrollable = () => row.scrollWidth - row.clientWidth > 1

    let currentIdx = 0
    let suppressScrollSync = false

    function paint() {
      const scrollable = isScrollable()
      if (thumb && total > 0) {
        thumb.style.transform = `translateX(${currentIdx * 100}%)`
      }
      if (progress) {
        progress.setAttribute('aria-valuenow', String(currentIdx))
      }
      // Disable both arrows when the row can't scroll, and at the ends otherwise,
      // so the edge buttons are never live when there's nowhere to go.
      if (prevBtn) prevBtn.disabled = !scrollable || currentIdx <= 0
      if (nextBtn) nextBtn.disabled = !scrollable || currentIdx >= total - 1
      node.dataset.activeIndex = String(currentIdx)
    }

    function setIndex(next, source) {
      const clamped = Math.max(0, Math.min(total - 1, next))
      if (clamped === currentIdx && source !== 'init') return
      currentIdx = clamped
      paint()
      if (track) {
        const card = cards[currentIdx]
        track(`${FEATURE_SLUG}:scroll`, {
          card_index: currentIdx,
          title: cardTitle(card),
          url: cardUrl(card),
        })
      }
    }

    // Scroll the chosen card into the row's visible area. A no-op when there's
    // no scrollable extent (cards already in view) — which is why the thumb
    // update lives in `paint()`, not here.
    function scrollToCurrent() {
      const card = cards[currentIdx]
      if (!card) return
      suppressScrollSync = true
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' })
      window.setTimeout(() => {
        suppressScrollSync = false
      }, 500)
    }

    paint()

    // Keep the index synced with the row's scroll position. IntersectionObserver
    // fires whenever any card's visibility changes, regardless of which element
    // is scrolling (row, page, parent).
    let rafId = 0
    const recompute = () => {
      if (suppressScrollSync) return
      if (rafId) return
      rafId = requestAnimationFrame(() => {
        rafId = 0
        const idx = currentIndex(row, cards)
        if (idx !== currentIdx) setIndex(idx, 'observer')
      })
    }
    if (typeof IntersectionObserver !== 'undefined') {
      const io = new IntersectionObserver(recompute, {
        threshold: [0, 0.25, 0.5, 0.75, 1],
      })
      for (const card of cards) io.observe(card)
    }
    row.addEventListener('scroll', recompute, { passive: true })
    window.addEventListener('scroll', recompute, { passive: true })
    // Re-evaluate scrollability + index when the breakpoint / card width changes.
    window.addEventListener(
      'resize',
      () => {
        paint()
        recompute()
      },
      { passive: true },
    )

    // Arrows step one card and clamp at the ends (no wrap).
    prevBtn?.addEventListener('click', () => {
      setIndex(currentIdx - 1, 'arrow')
      scrollToCurrent()
    })
    nextBtn?.addEventListener('click', () => {
      setIndex(currentIdx + 1, 'arrow')
      scrollToCurrent()
    })

    // Product click tracking — fires before the browser commits navigation so
    // the event lands in PostHog. Native link semantics (cmd-click, middle-
    // click) still work — we never preventDefault.
    if (track) {
      for (const card of cards) {
        const link = card.querySelector(CARD_LINK_SELECTOR)
        link?.addEventListener('click', () => {
          track(`${FEATURE_SLUG}:product_click`, {
            card_index: Number(card.dataset.spectrumCardIndex || '0'),
            title: cardTitle(card),
            url: cardUrl(card),
          })
        })
      }
    }

    // Quick-add — progressive enhancement over the native cart-add form. When
    // the Spectrum cart SDK is present, add the variant in-place and keep the
    // shopper on the page; otherwise the native form POST proceeds.
    const quickAddForms = node.querySelectorAll(QUICK_ADD_SELECTOR)
    for (const form of quickAddForms) {
      form.addEventListener('submit', (ev) => {
        const variantId = form.querySelector('input[name="id"]')?.value || ''
        const card = form.closest(CARD_SELECTOR)
        const cardIdx = Number(card?.dataset.spectrumCardIndex || '0')
        if (track) {
          track(`${FEATURE_SLUG}:quick_add_click`, { card_index: cardIdx, variant_id: variantId })
        }
        const cart = window.Spectrum && window.Spectrum.cart
        if (cart && typeof cart.add === 'function' && variantId) {
          ev.preventDefault()
          Promise.resolve(cart.add({ id: variantId, quantity: 1 })).catch((err) => {
            // eslint-disable-next-line no-console
            console.warn(`[${FEATURE_SLUG}] quick-add failed`, err)
          })
        }
      })
    }

    // Card impression — fires once per card per page-load when ≥50% visible.
    // Gated by the per-brand impressions kill-switch via the `_impression`
    // event-name suffix (see snippet-library CLAUDE.md).
    if (track && typeof IntersectionObserver !== 'undefined') {
      const seen = new Set()
      const io = new IntersectionObserver(
        (entries) => {
          for (const e of entries) {
            if (!e.isIntersecting) continue
            const card = e.target
            const idx = Number(card.dataset.spectrumCardIndex || '0')
            if (seen.has(idx)) continue
            seen.add(idx)
            track(`${FEATURE_SLUG}:product_impression`, {
              card_index: idx,
              title: cardTitle(card),
              url: cardUrl(card),
            })
          }
        },
        { threshold: 0.5, root: row },
      )
      for (const card of cards) io.observe(card)
    }
  }

  if (typeof globalThis !== 'undefined' && globalThis.__SAI_TEST_HARNESS__ === true) {
    globalThis.__saiJaqo0kgp = {
      currentIndex,
      safeTrack,
    }
  }

  const snippetApi = window.__spectrumAi?.snippet
  const containers = document.querySelectorAll(
    `[data-spectrum-instance-id][data-spectrum-snippet-id="${SNIPPET_ID}"]`,
  )
  for (const node of containers) {
    let track = noopTrack
    if (snippetApi && typeof snippetApi.bind === 'function') {
      const handles = snippetApi.bind(node, () => {
        // No variant-resolution work needed — the row is server-rendered and
        // every prop the runtime might swap is captured by the wrapper
        // re-render on variant change.
      })
      if (handles?.track) track = safeTrack(handles.track)
    }
    initCarousel(node, track)
  }
})()
